import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiResponse } from "@/helpers/axios";
import { axiosInstance, handleAxiosError } from "@/helpers/axios";
import type {
  StatisticsAccountGeoPoint,
  StatisticsOverviewData,
} from "../_types/statistics-api.types";
import {
  boundsToApiDateRange,
  previousPeriodInclusive,
} from "../_utils/statisticsDateRangeApi.util";

type Bounds = { start: Date; end: Date };

export function useStatisticsDashboard(bounds: Bounds) {
  const [overview, setOverview] = useState<StatisticsOverviewData | null>(null);
  const [prevOverview, setPrevOverview] =
    useState<StatisticsOverviewData | null>(null);
  const [geoPoints, setGeoPoints] = useState<
    StatisticsAccountGeoPoint[] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const geoLoaded = useRef(false);

  const loadOverview = useCallback(async (b: Bounds) => {
    const { from, to } = boundsToApiDateRange(b);
    const prev = previousPeriodInclusive(from, to);

    const [curRes, prevRes] = await Promise.all([
      axiosInstance.get<ApiResponse<StatisticsOverviewData>>(
        "/v2/statistics/overview",
        { params: { from, to } },
      ),
      axiosInstance.get<ApiResponse<StatisticsOverviewData>>(
        "/v2/statistics/overview",
        { params: { from: prev.from, to: prev.to } },
      ),
    ]);

    if (!curRes.data.success || !curRes.data.data) {
      throw new Error(curRes.data.message ?? "Failed to load statistics");
    }
    if (!prevRes.data.success || !prevRes.data.data) {
      throw new Error(prevRes.data.message ?? "Failed to load previous period");
    }

    setOverview(curRes.data.data);
    setPrevOverview(prevRes.data.data);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await loadOverview(bounds);
      } catch (e) {
        if (cancelled) return;
        const api = handleAxiosError(e);
        setError(api?.message ?? (e instanceof Error ? e.message : "Error"));
        setOverview(null);
        setPrevOverview(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bounds, loadOverview]);

  useEffect(() => {
    if (geoLoaded.current) return;
    geoLoaded.current = true;
    (async () => {
      try {
        const { data } = await axiosInstance.get<
          ApiResponse<{ points: StatisticsAccountGeoPoint[] }>
        >("/v2/statistics/account-geo");
        if (data.success && data.data?.points) {
          setGeoPoints(data.data.points);
        } else {
          setGeoPoints([]);
        }
      } catch {
        setGeoPoints([]);
      }
    })();
  }, []);

  return {
    overview,
    prevOverview,
    geoPoints,
    loading,
    error,
  };
}
